/**
 * Tela de formulário de um orçamento, em endereço próprio.
 *
 * Papel: monta `/orcamentos/novo` e `/orcamentos/:id`, com a mesma verificação
 * de nível `editar` que `PaginaOrcamentos` aplica aos botões da lista — aqui
 * ela decide se o formulário chega a aparecer, porque o endereço é digitável
 * direto, sem passar pelo botão que o esconderia.
 *
 * Como usar: o `id` vem de `useParams`; ausente, a tela cria um orçamento
 * novo. Presente e sem correspondência entre os orçamentos carregados, a
 * tela mostra que o registro não existe, em vez de abrir um formulário em
 * branco sob um número que não é o dele ou travar numa tela sem nada — o que
 * cobre tanto o `id` apagado por outra aba quanto o digitado errado.
 *
 * As duas consultas de que `FormularioOrcamento` depende — `useResidencias` e
 * a listagem de orçamentos — são esperadas **aqui, em qualquer um dos dois
 * modos**, antes de o formulário chegar a montar, e é aqui que as casas, a
 * casa sugerida e o número sugerido são calculados. `FormularioOrcamento` lê a
 * casa e o número sugerido só uma vez, no `defaultValues` do primeiro render;
 * montá-lo com qualquer uma das duas consultas ainda a caminho trava a casa em
 * branco e o número em `001/AAAA` para sempre, porque o react-hook-form não
 * refaz esse cálculo depois.
 *
 * As casas do `<select>` passam por `casasQueRecebemRegistro`: só as que não
 * contêm outra, mais a do orçamento aberto. Quando `useResidencias` falha —
 * quem não tem `ver` em Cadastros-base recebe 403 dela —, o formulário abre
 * assim mesmo, com as casas tiradas dos orçamentos carregados por
 * `casasDistintas`; a falha da listagem de orçamentos continua trocando o
 * formulário pelo aviso de erro. A casa sugerida para um orçamento novo é a do
 * mais recente cuja casa está entre as oferecidas, e a primeira delas quando
 * nenhum tem — a lista chega ordenada pelo lugar que contém cada casa, e a
 * primeira dela não é escolha de ninguém.
 *
 * Imprimir, duplicar e excluir vivem aqui, ao lado do título, e não dentro de
 * `FormularioOrcamento`: são ações sobre o registro já gravado, e um
 * orçamento novo só ganha as duas últimas depois de existir. "Duplicar" lê os
 * valores atuais do formulário pela `ref` — `alcaDoFormulario` —, e não o
 * `registro` gravado, para copiar também o que a pessoa tiver alterado e ainda
 * não salvo. Toda saída depois de gravar, duplicar ou excluir volta à lista
 * com `replace`, para o Voltar do navegador não cair de novo num formulário já
 * salvo ou num orçamento que deixou de existir; cancelar volta sem `replace`.
 * A exclusão recusada mostra a mensagem da API dentro da caixa de
 * confirmação, que continua aberta.
 */
import {
  type NovoOrcamento,
  acessoAlcanca,
  novoOrcamentoSchema,
  proximoNumero,
} from '@concierge/shared';
import { useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import { ErroDaApi } from '@/shared/api';
import { useAcesso } from '@/shared/hooks';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog';
import { Button } from '@/shared/ui/button';
import { CabecalhoDoModulo } from '@/shared/ui/cabecalho-do-modulo';
import {
  casasDistintas,
  casasQueRecebemRegistro,
  useResidencias,
} from '@/modules/cadastros-base';
import { buscarFotoDoItem } from '../api/orcamentos';
import {
  type AlcaDoFormularioOrcamento,
  type CasaDoOrcamento,
  FormularioOrcamento,
} from '../components/formulario-orcamento';
import {
  useExcluirOrcamento,
  useOrcamentos,
  useSalvarOrcamento,
} from '../hooks/usar-orcamentos';

/**
 * Aviso central de espera, com o cabeçalho da tela por trás.
 *
 * @param mensagem - O que dizer enquanto se espera.
 * @returns O cabeçalho e a mensagem, sem formulário nenhum.
 */
function TelaDeEspera({ mensagem }: { mensagem: string }) {
  return (
    <div>
      <CabecalhoDoModulo titulo="Orçamento" voltarPara="/orcamentos" />
      <p className="text-sm text-muted-foreground">{mensagem}</p>
    </div>
  );
}

/**
 * Aviso central de falha numa das consultas de que o formulário depende.
 *
 * @param erro - O que a consulta rejeitou.
 * @param onTentarDeNovo - Chamado para repetir a consulta que falhou.
 * @returns O cabeçalho e a explicação da falha, com o botão de tentar de novo.
 */
function TelaDeErroDeConsulta({
  erro,
  onTentarDeNovo,
}: {
  erro: unknown;
  onTentarDeNovo: () => void;
}) {
  return (
    <div>
      <CabecalhoDoModulo titulo="Orçamento" voltarPara="/orcamentos" />
      <div className="rounded-md border border-dashed border-border bg-card px-6 py-12 text-center">
        <p className="font-serif text-lg text-foreground">Não foi possível carregar</p>
        <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
          {erro instanceof ErroDaApi
            ? erro.message
            : 'A consulta falhou antes de chegar ao servidor.'}
        </p>
        <Button variant="outline" className="mt-4" onClick={onTentarDeNovo}>
          Tentar de novo
        </Button>
      </div>
    </div>
  );
}

/**
 * Página do formulário de orçamento.
 *
 * @returns O cabeçalho com as ações do registro e o formulário, ou o estado
 *   que a permissão, o carregamento ou a busca pelo `id` impuserem.
 */
export function PaginaFormularioOrcamento() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [aRemover, setARemover] = useState(false);
  const [erroDaAcao, setErroDaAcao] = useState<string | null>(null);
  const [erroDaRemocao, setErroDaRemocao] = useState<string | null>(null);
  const alcaDoFormulario = useRef<AlcaDoFormularioOrcamento>(null);

  const acesso = useAcesso();
  const podeEditar =
    acesso.data !== undefined && acessoAlcanca(acesso.data, 'orcamentos', 'editar');

  const consultaOrcamentos = useOrcamentos();
  const consultaCasas = useResidencias();
  const duplicar = useSalvarOrcamento();
  const excluir = useExcluirOrcamento();

  /** Volta para a lista depois de gravar ou excluir, sem deixar o formulário no histórico. */
  function handleVoltarParaLista() {
    void navigate('/orcamentos', { replace: true });
  }

  /** Volta para a lista sem gravar nada, como o Voltar do cabeçalho. */
  function handleCancelar() {
    void navigate('/orcamentos');
  }

  /** Abre ou fecha a confirmação de exclusão, com a falha anterior limpa. */
  function handleRemocaoOpenChange(aberto: boolean) {
    setErroDaRemocao(null);
    setARemover(aberto);
  }

  /** Abre a caixa de impressão do navegador, o mesmo caminho até o PDF. */
  function handleImprimir() {
    window.print();
  }

  if (acesso.isPending) return <TelaDeEspera mensagem="Verificando permissão…" />;

  if (!podeEditar) {
    return (
      <div>
        <CabecalhoDoModulo titulo="Orçamento" voltarPara="/orcamentos" />
        <div className="rounded-md border border-dashed border-border bg-card px-6 py-12 text-center">
          <p className="font-serif text-lg text-foreground">Acesso restrito</p>
          <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
            Esta tela exige o nível editar em Orçamentos. Se você precisa dela, peça o nível a
            quem administra.
          </p>
        </div>
      </div>
    );
  }

  if (consultaOrcamentos.isPending || consultaCasas.isPending) {
    return <TelaDeEspera mensagem="Carregando…" />;
  }

  if (consultaOrcamentos.error !== null) {
    return (
      <TelaDeErroDeConsulta
        erro={consultaOrcamentos.error}
        onTentarDeNovo={() => void consultaOrcamentos.refetch()}
      />
    );
  }

  const orcamentos = consultaOrcamentos.data;
  const numeroSugerido = proximoNumero(
    orcamentos.map((item) => item.numero),
    new Date().getFullYear(),
  );
  const aberto = id === undefined ? undefined : orcamentos.find((item) => item.id === id);
  const casaDoRegistro = aberto?.residencia ?? null;
  const casasConhecidas: readonly CasaDoOrcamento[] =
    consultaCasas.data ??
    casasDistintas([...orcamentos.map((item) => item.residencia), casaDoRegistro]);
  const casas = casasQueRecebemRegistro(casasConhecidas, casaDoRegistro?.id ?? null);
  const idsOferecidos = new Set(casas.map((casa) => casa.id));
  const casaSugerida =
    orcamentos.find((item) => idsOferecidos.has(item.residencia.id))?.residencia.id ??
    casas[0]?.id ??
    '';

  if (id !== undefined) {
    const orcamento = aberto;

    if (orcamento === undefined) {
      return (
        <div>
          <CabecalhoDoModulo titulo="Orçamento" voltarPara="/orcamentos" />
          <div className="rounded-md border border-dashed border-border bg-card px-6 py-12 text-center">
            <p className="font-serif text-lg text-foreground">Orçamento não encontrado</p>
            <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
              Ele pode ter sido excluído nesta ou em outra sessão.
            </p>
          </div>
        </div>
      );
    }

    // Alias com o tipo já estreitado: `orcamento` segue como `Orcamento |
    // undefined` para o TypeScript dentro das funções abaixo, porque a
    // verificação acima não atravessa o limite de uma função aninhada.
    // `registro` nasce depois do `return` que descarta `undefined`, e por
    // nunca ser reatribuído carrega o tipo estreitado para dentro delas sem
    // precisar de `!`.
    const registro = orcamento;

    /**
     * Copia o que está na tela — inclusive edição que ninguém salvou ainda —
     * num orçamento novo, com número novo e situação de volta ao início.
     *
     * A foto de um item que já tinha uma, e que a pessoa não trocou nesta
     * sessão, não está nos valores do formulário — `FormularioOrcamento` não a
     * carrega ao abrir —, e por isso precisa ser buscada aqui antes de copiar;
     * sem essa busca, o orçamento duplicado perderia a foto em silêncio.
     */
    async function handleDuplicar() {
      setErroDaAcao(null);

      const valoresAtuais = alcaDoFormulario.current?.obterValores();
      if (valoresAtuais === undefined) return;

      let dados: NovoOrcamento;
      try {
        dados = novoOrcamentoSchema.parse(valoresAtuais);
      } catch {
        setErroDaAcao('Corrija os campos com erro antes de duplicar.');
        return;
      }

      const numero = proximoNumero(
        (consultaOrcamentos.data ?? []).map((item) => item.numero),
        new Date().getFullYear(),
      );

      try {
        const itens = await Promise.all(
          dados.itens.map(async (item) => {
            const { id: idDoItem, ...resto } = item;
            const gravado =
              idDoItem === undefined
                ? undefined
                : registro.itens.find((original) => original.id === idDoItem);

            // Ausente é o único estado que o formulário produz para um item
            // que já tinha foto e não foi trocado: buscar aqui é o que evita
            // a cópia sair sem a imagem que a pessoa nunca pediu para tirar.
            if (resto.foto === undefined && gravado?.temFoto === true) {
              const { foto } = await buscarFotoDoItem(gravado.id);
              return { ...resto, foto: foto ?? undefined };
            }
            return resto;
          }),
        );

        await duplicar.mutateAsync({
          id: null,
          dados: { ...dados, numero, status: 'orcamento', itens },
        });
        handleVoltarParaLista();
      } catch (erro) {
        setErroDaAcao(
          erro instanceof ErroDaApi
            ? erro.message
            : 'Não foi possível copiar uma das fotos, ou a duplicação falhou antes de chegar ao servidor.',
        );
      }
    }

    /** Apaga o orçamento aberto e volta para a lista, se a API aceitar. */
    async function handleConfirmarRemocao() {
      setErroDaRemocao(null);
      try {
        await excluir.mutateAsync(registro.id);
        handleVoltarParaLista();
      } catch (erro) {
        setErroDaRemocao(
          erro instanceof ErroDaApi
            ? erro.message
            : 'A exclusão falhou antes de chegar ao servidor.',
        );
      }
    }

    return (
      <div>
        <CabecalhoDoModulo
          titulo="Editar orçamento"
          subtitulo={registro.numero}
          voltarPara="/orcamentos"
          acoes={
            <>
              <Button type="button" variant="outline" onClick={handleImprimir}>
                🖨 Imprimir / PDF
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => void handleDuplicar()}
                disabled={duplicar.isPending}
              >
                ⧉ Duplicar
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={() => handleRemocaoOpenChange(true)}
                disabled={excluir.isPending}
              >
                🗑 Excluir
              </Button>
            </>
          }
        />

        {erroDaAcao !== null && (
          <p className="mb-4 rounded-sm border border-perigo/40 bg-perigo/5 p-3 text-sm text-perigo">
            {erroDaAcao}
          </p>
        )}

        <FormularioOrcamento
          ref={alcaDoFormulario}
          key={registro.id}
          orcamento={registro}
          casas={casas}
          casaSugerida={casaSugerida}
          numeroSugerido={numeroSugerido}
          onSalvo={handleVoltarParaLista}
          onCancelar={handleCancelar}
        />

        <AlertDialog open={aRemover} onOpenChange={handleRemocaoOpenChange}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Excluir o orçamento {registro.numero}?</AlertDialogTitle>
              <AlertDialogDescription>
                Os itens da proposta vão junto, com as imagens deles. A ação não pode ser
                desfeita.
              </AlertDialogDescription>
            </AlertDialogHeader>

            {erroDaRemocao !== null && (
              <p className="text-sm text-destructive" role="alert">
                {erroDaRemocao}
              </p>
            )}

            <AlertDialogFooter>
              <AlertDialogCancel disabled={excluir.isPending}>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                onClick={(evento) => {
                  evento.preventDefault();
                  void handleConfirmarRemocao();
                }}
                disabled={excluir.isPending}
              >
                {excluir.isPending ? 'Excluindo…' : 'Excluir'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    );
  }

  return (
    <div>
      <CabecalhoDoModulo
        titulo="Novo orçamento"
        subtitulo={numeroSugerido}
        voltarPara="/orcamentos"
        acoes={
          <Button type="button" variant="outline" onClick={handleImprimir}>
            🖨 Imprimir / PDF
          </Button>
        }
      />
      <FormularioOrcamento
        key="novo"
        orcamento={null}
        casas={casas}
        casaSugerida={casaSugerida}
        numeroSugerido={numeroSugerido}
        onSalvo={handleVoltarParaLista}
        onCancelar={handleCancelar}
      />
    </div>
  );
}
